import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

interface QuizCreateProps {
  onCreated: () => void;
  onClose: () => void;
  userId?: string;
}

const DRAFT_KEY = 'quiz_create_draft';

interface QuizDraft {
  question: string;
  choices: string[];
  correctIndex: number | null;
  explanation: string;
}

interface CategoryItem {
  id: string;
  category_name: string;
  is_favorite: boolean;
}

// ─── 軽量ドロップダウン（完全メモ化） ───────────────────────────────────────
interface LightDropdownProps {
  label: string;
  placeholder: string;
  value: string;
  items: CategoryItem[];
  loading: boolean;
  onSelect: (name: string) => void;
  onAdd: (name: string) => Promise<void>;
}

const LightDropdown = memo(({ label, placeholder, value, items, loading, onSelect, onAdd }: LightDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
        setAdding(false);
        setNewName('');
        setAddError('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(
    () => items.filter((i) => i.category_name.toLowerCase().includes(search.toLowerCase())),
    [items, search]
  );

  const handleAdd = useCallback(async () => {
    if (!newName.trim()) { setAddError('名前を入力してください'); return; }
    setAddLoading(true);
    setAddError('');
    try {
      await onAdd(newName.trim());
      setNewName('');
      setAdding(false);
    } catch (e: any) {
      setAddError(e.code === '23505' ? '既に存在します' : '追加に失敗しました');
    } finally {
      setAddLoading(false);
    }
  }, [newName, onAdd]);

  const handleSelect = useCallback((name: string) => {
    onSelect(name);
    setOpen(false);
    setSearch('');
  }, [onSelect]);

  const toggleOpen = useCallback(() => setOpen((v) => !v), []);
  const startAdding = useCallback(() => setAdding(true), []);
  const cancelAdding = useCallback(() => {
    setAdding(false);
    setNewName('');
    setAddError('');
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full px-4 py-2.5 text-left bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:border-teal-500 dark:hover:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all flex items-center justify-between cursor-pointer"
      >
        <span className={value ? 'text-gray-900 dark:text-white text-sm' : 'text-gray-400 text-sm'}>
          {value || placeholder}
        </span>
        <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-gray-400`}></i>
      </button>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-72 flex flex-col">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="検索..."
                className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 dark:text-white"
                autoFocus
              />
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <p className="p-3 text-center text-xs text-gray-400">読み込み中...</p>
            ) : filtered.length === 0 ? (
              <p className="p-3 text-center text-xs text-gray-400">{search ? '該当なし' : 'まだありません'}</p>
            ) : (
              <div className="py-1">
                {filtered.map((item) => (
                  <DropdownItem
                    key={item.id}
                    item={item}
                    isSelected={value === item.category_name}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="p-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            {adding ? (
              <div className="space-y-1.5">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') cancelAdding(); }}
                  placeholder="新しい名前"
                  className="w-full px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 dark:text-white"
                  autoFocus
                />
                {addError && <p className="text-xs text-red-500">{addError}</p>}
                <div className="flex gap-1.5">
                  <button type="button" onClick={handleAdd} disabled={addLoading}
                    className="flex-1 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                    {addLoading ? '追加中...' : '追加'}
                  </button>
                  <button type="button" onClick={cancelAdding}
                    className="flex-1 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap">
                    キャンセル
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={startAdding}
                className="w-full py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs rounded-md transition-colors flex items-center justify-center gap-1 cursor-pointer whitespace-nowrap">
                <i className="ri-add-line"></i>新しく追加
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}, (prev, next) => {
  return prev.value === next.value && prev.items === next.items && prev.loading === next.loading;
});
LightDropdown.displayName = 'LightDropdown';

// ─── ドロップダウンアイテム（個別メモ化） ──────────────────────────────────
interface DropdownItemProps {
  item: CategoryItem;
  isSelected: boolean;
  onSelect: (name: string) => void;
}
const DropdownItem = memo(({ item, isSelected, onSelect }: DropdownItemProps) => {
  const handleClick = useCallback(() => onSelect(item.category_name), [item.category_name, onSelect]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer flex items-center gap-2 ${isSelected ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300' : 'text-gray-800 dark:text-white'}`}
    >
      {item.is_favorite && <i className="ri-star-fill text-yellow-400 text-xs shrink-0"></i>}
      {item.category_name}
    </button>
  );
});
DropdownItem.displayName = 'DropdownItem';

// ─── 問題ナビゲーションボタン（個別メモ化） ────────────────────────────────
interface NavBtnProps {
  index: number;
  active: boolean;
  onClick: (i: number) => void;
}
const NavBtn = memo(({ index, active, onClick }: NavBtnProps) => {
  const handleClick = useCallback(() => onClick(index), [index, onClick]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-teal-600 text-white ring-2 ring-teal-300 dark:ring-teal-600'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
      }`}
    >
      問題{index + 1}
    </button>
  );
}, (prev, next) => prev.index === next.index && prev.active === next.active);
NavBtn.displayName = 'NavBtn';

// ─── 選択肢入力（非制御コンポーネント＋ref同期方式でバグ完全排除） ──────────
interface ChoiceInputProps {
  index: number;
  value: string;
  label: string;
  onCommit: (index: number, value: string) => void;
}
const ChoiceInput = memo(({ index, value, label, onCommit }: ChoiceInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // 外部からvalueが変わったとき（問題切り替え時など）のみDOMを同期
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (inputRef.current && prevValueRef.current !== value) {
      // フォーカス中は上書きしない（入力中の文字を守る）
      if (document.activeElement !== inputRef.current) {
        inputRef.current.value = value;
      }
      prevValueRef.current = value;
    }
  }, [value]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.slice(0, 200);
    if (e.target.value.length > 200) e.target.value = v;
    prevValueRef.current = v;
    onCommit(index, v);
  }, [index, onCommit]);

  return (
    <div className="flex items-center gap-2.5">
      <span className="w-7 h-7 flex items-center justify-center bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 rounded-full text-xs font-bold shrink-0">
        {label}
      </span>
      <input
        ref={inputRef}
        type="text"
        defaultValue={value}
        onChange={handleChange}
        placeholder={`選択肢${label}を入力`}
        maxLength={200}
        className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-gray-900 dark:text-white"
      />
    </div>
  );
});
ChoiceInput.displayName = 'ChoiceInput';

// ─── 正解ボタン（個別メモ化） ──────────────────────────────────────────────
interface CorrectButtonProps {
  index: number;
  label: string;
  choiceText: string;
  isSelected: boolean;
  onSelect: (index: number) => void;
}
const CorrectButton = memo(({ index, label, choiceText, isSelected, onSelect }: CorrectButtonProps) => {
  const handleClick = useCallback(() => onSelect(index), [index, onSelect]);
  const trimmed = choiceText.trim();
  const preview = trimmed ? `: ${trimmed.slice(0, 15)}${trimmed.length > 15 ? '...' : ''}` : '';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!trimmed}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${isSelected ? 'bg-teal-600 text-white ring-2 ring-teal-300 dark:ring-teal-600' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'}`}
    >
      {label}{preview}
    </button>
  );
}, (prev, next) => prev.isSelected === next.isSelected && prev.choiceText === next.choiceText);
CorrectButton.displayName = 'CorrectButton';

// ─── 問題フォーム ──────────────────────────────────────────────────────────
interface QuizFormProps {
  quiz: QuizDraft;
  index: number;
  totalCount: number;
  onUpdate: (index: number, updates: Partial<QuizDraft>) => void;
  onDelete: (index: number) => void;
}
const QuizForm = memo(({ quiz, index, totalCount, onUpdate, onDelete }: QuizFormProps) => {
  const choiceLabels = ['A', 'B', 'C', 'D'];

  // 問題文・解説は制御コンポーネントのまま（textarea）
  const handleQuestionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onUpdate(index, { question: e.target.value.slice(0, 500) });
  }, [index, onUpdate]);

  const handleExplanationChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onUpdate(index, { explanation: e.target.value.slice(0, 500) });
  }, [index, onUpdate]);

  // 選択肢はonCommit経由で更新（ChoiceInputが非制御なので安全）
  const handleChoiceCommit = useCallback((choiceIdx: number, value: string) => {
    onUpdate(index, {
      choices: quiz.choices.map((c, i) => (i === choiceIdx ? value : c)),
    });
  }, [index, quiz.choices, onUpdate]);

  const handleCorrectIndexChange = useCallback((correctIdx: number) => {
    onUpdate(index, { correctIndex: correctIdx });
  }, [index, onUpdate]);

  const handleDeleteClick = useCallback(() => {
    onDelete(index);
  }, [index, onDelete]);

  const correctAnswerPreview = useMemo(() => {
    if (quiz.correctIndex !== null && quiz.choices[quiz.correctIndex]?.trim()) {
      const txt = quiz.choices[quiz.correctIndex].trim();
      return `正解: ${choiceLabels[quiz.correctIndex]}. ${txt}`;
    }
    return null;
  }, [quiz.correctIndex, quiz.choices]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-300 dark:border-gray-600 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-gray-800 dark:text-white">問題{index + 1}の内容</h4>
        {totalCount > 1 && (
          <button type="button" onClick={handleDeleteClick}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-delete-bin-line text-sm"></i>削除
          </button>
        )}
      </div>

      {/* 問題文 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          問題文 <span className="text-red-500">*</span>
        </label>
        <textarea
          value={quiz.question}
          onChange={handleQuestionChange}
          placeholder="問題文を入力してください"
          rows={3}
          maxLength={500}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-gray-900 dark:text-white resize-none"
        />
        <p className="text-xs text-gray-400 mt-1 text-right">{quiz.question.length}/500</p>
      </div>

      {/* 選択肢 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          四択の選択肢 <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2.5">
          {quiz.choices.map((choice, idx) => (
            <ChoiceInput
              key={`${index}-${idx}`}
              index={idx}
              value={choice}
              label={choiceLabels[idx]}
              onCommit={handleChoiceCommit}
            />
          ))}
        </div>
      </div>

      {/* 正解選択 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          正解の選択肢 <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {choiceLabels.map((label, idx) => (
            <CorrectButton
              key={idx}
              index={idx}
              label={label}
              choiceText={quiz.choices[idx]}
              isSelected={quiz.correctIndex === idx}
              onSelect={handleCorrectIndexChange}
            />
          ))}
        </div>
        {correctAnswerPreview && (
          <p className="text-xs text-teal-600 dark:text-teal-400 mt-2">
            <i className="ri-checkbox-circle-fill mr-1"></i>{correctAnswerPreview}
          </p>
        )}
      </div>

      {/* 解説 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">解説（任意）</label>
        <textarea
          value={quiz.explanation}
          onChange={handleExplanationChange}
          placeholder="解説を入力してください（任意）"
          rows={2}
          maxLength={500}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-gray-900 dark:text-white resize-none"
        />
        <p className="text-xs text-gray-400 mt-1 text-right">{quiz.explanation.length}/500</p>
      </div>
    </div>
  );
});
QuizForm.displayName = 'QuizForm';

// ─── メインコンポーネント ──────────────────────────────────────────────────
export default function QuizCreate({ onCreated, onClose, userId }: QuizCreateProps) {
  const { user } = useAuth();

  const [subject, setSubject] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [quizList, setQuizList] = useState<QuizDraft[]>([
    { question: '', choices: ['', '', '', ''], correctIndex: null, explanation: '' },
  ]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [subjects, setSubjects] = useState<CategoryItem[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [catLoading, setCatLoading] = useState(true);

  // ─── カテゴリ一括フェッチ（マウント時1回のみ） ───────────────────────────
  useEffect(() => {
    if (!user) { setCatLoading(false); return; }
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      setCatLoading(true);
      try {
        const { data } = await supabase
          .from('user_categories')
          .select('id, category_name, category_type, is_favorite, user_id')
          .order('category_name', { ascending: true })
          .abortSignal(controller.signal);

        if (cancelled || !data) return;

        const dedup = (type: 'subject' | 'category') => {
          const map = new Map<string, CategoryItem>();
          for (const row of data.filter((r) => r.category_type === type)) {
            const ex = map.get(row.category_name);
            if (!ex || row.user_id === user.id) {
              map.set(row.category_name, { id: row.id, category_name: row.category_name, is_favorite: row.is_favorite });
            }
          }
          return Array.from(map.values()).sort((a, b) => {
            if (a.is_favorite && !b.is_favorite) return -1;
            if (!a.is_favorite && b.is_favorite) return 1;
            return a.category_name.localeCompare(b.category_name, 'ja');
          });
        };

        setSubjects(dedup('subject'));
        setCategories(dedup('category'));
      } catch (e: any) {
        if (e.name !== 'AbortError') console.error(e);
      } finally {
        if (!cancelled) setCatLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [user?.id]);

  // ─── カテゴリ追加 ────────────────────────────────────────────────────────
  const addSubject = useCallback(async (name: string) => {
    if (!user) throw new Error('ログインが必要です');
    const { data: ex } = await supabase.from('user_categories').select('id').eq('category_name', name).eq('category_type', 'subject').maybeSingle();
    if (ex) throw Object.assign(new Error('既に存在します'), { code: '23505' });
    const { data, error: e } = await supabase.from('user_categories').insert({ user_id: user.id, category_name: name, category_type: 'subject', is_favorite: false }).select('id, category_name, is_favorite').single();
    if (e) throw e;
    setSubjects((prev) => [...prev, { id: data.id, category_name: data.category_name, is_favorite: false }].sort((a, b) => a.category_name.localeCompare(b.category_name, 'ja')));
  }, [user?.id]);

  const addCategory = useCallback(async (name: string) => {
    if (!user) throw new Error('ログインが必要です');
    const { data: ex } = await supabase.from('user_categories').select('id').eq('category_name', name).eq('category_type', 'category').maybeSingle();
    if (ex) throw Object.assign(new Error('既に存在します'), { code: '23505' });
    const { data, error: e } = await supabase.from('user_categories').insert({ user_id: user.id, category_name: name, category_type: 'category', is_favorite: false }).select('id, category_name, is_favorite').single();
    if (e) throw e;
    setCategories((prev) => [...prev, { id: data.id, category_name: data.category_name, is_favorite: false }].sort((a, b) => a.category_name.localeCompare(b.category_name, 'ja')));
  }, [user?.id]);

  // ─── 下書き復元（初回のみ） ──────────────────────────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (parsed.subject) setSubject(parsed.subject);
      if (parsed.subcategory) setSubcategory(parsed.subcategory);
      if (typeof parsed.isPublic === 'boolean') setIsPublic(parsed.isPublic);
      if (Array.isArray(parsed.quizList) && parsed.quizList.length > 0) setQuizList(parsed.quizList);
      if (typeof parsed.currentIndex === 'number') setCurrentIndex(parsed.currentIndex);
    } catch { /* ignore */ }
  }, []);

  // ─── 下書き保存（debounce 2000ms） ──────────────────────────────────────
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ subject, subcategory, isPublic, quizList, currentIndex }));
      } catch { /* ignore */ }
    }, 2000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [subject, subcategory, isPublic, quizList, currentIndex]);

  // ─── 問題操作 ────────────────────────────────────────────────────────────
  const handleQuizUpdate = useCallback((index: number, updates: Partial<QuizDraft>) => {
    setQuizList((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  }, []);

  const addNewQuiz = useCallback(() => {
    setQuizList((prev) => [...prev, { question: '', choices: ['', '', '', ''], correctIndex: null, explanation: '' }]);
    setCurrentIndex((prev) => prev + 1);
  }, []);

  const deleteQuiz = useCallback((index: number) => {
    if (quizList.length === 1) return;
    setQuizList((prev) => prev.filter((_, i) => i !== index));
    setCurrentIndex((prev) => Math.min(prev, quizList.length - 2));
  }, [quizList.length]);

  const handleNavClick = useCallback((i: number) => setCurrentIndex(i), []);

  const handleSubjectSelect = useCallback((name: string) => {
    setSubject(name);
    setSubcategory('');
  }, []);

  const togglePublic = useCallback(() => setIsPublic(true), []);
  const togglePrivate = useCallback(() => setIsPublic(false), []);

  // ─── 保存 ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!subject) { setError('科目を選択してください'); return; }
    for (let i = 0; i < quizList.length; i++) {
      const q = quizList[i];
      if (!q.question.trim()) { setError(`問題${i + 1}: 問題文を入力してください`); setCurrentIndex(i); return; }
      if (q.choices.some((c) => !c.trim())) { setError(`問題${i + 1}: すべての選択肢を入力してください`); setCurrentIndex(i); return; }
      if (q.correctIndex === null) { setError(`問題${i + 1}: 正解の選択肢を選んでください`); setCurrentIndex(i); return; }
    }
    setSaving(true);
    setError('');
    try {
      const rows = quizList.map((q) => ({
        subject,
        subcategory: subcategory || null,
        question: q.question.trim(),
        choices: q.choices.map((c) => c.trim()),
        answer: q.choices[q.correctIndex!].trim(),
        explanation: q.explanation.trim() || null,
        user_id: userId || null,
        is_public: isPublic,
      }));
      const { error: dbError } = await supabase.from('quiz_questions').insert(rows);
      if (dbError) throw dbError;
      localStorage.removeItem(DRAFT_KEY);
      onCreated();
    } catch {
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  }, [subject, subcategory, isPublic, quizList, userId, onCreated]);

  const currentQuiz = quizList[currentIndex];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-100 dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="sticky top-0 bg-gray-100 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white">クイズを作成</h3>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer">
            <i className="ri-close-line text-xl text-gray-600 dark:text-gray-300"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          {/* ① 科目選択 */}
          <LightDropdown
            label="① 科目を選択"
            placeholder="科目を選択"
            value={subject}
            items={subjects}
            loading={catLoading}
            onSelect={handleSubjectSelect}
            onAdd={addSubject}
          />

          {/* ② カテゴリ選択 */}
          {subject && (
            <LightDropdown
              label={`${subject}のカテゴリ（任意）`}
              placeholder="カテゴリを選択"
              value={subcategory}
              items={categories}
              loading={catLoading}
              onSelect={setSubcategory}
              onAdd={addCategory}
            />
          )}

          {/* ③ 公開設定 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">② 公開設定</label>
            <div className="flex gap-3">
              <button type="button" onClick={togglePublic}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${isPublic ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-400 text-teal-700 dark:text-teal-400 ring-2 ring-teal-300 dark:ring-teal-600' : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                <i className="ri-global-line text-base"></i>全体公開
              </button>
              <button type="button" onClick={togglePrivate}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${!isPublic ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-400 text-amber-700 dark:text-amber-400 ring-2 ring-amber-300 dark:ring-amber-600' : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                <i className="ri-lock-line text-base"></i>自分だけ
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">{isPublic ? '全ユーザーに公開されます' : '自分だけが閲覧できます'}</p>
          </div>

          {/* ④ 問題ナビゲーション */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-300 dark:border-gray-600 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-gray-800 dark:text-white">③ 問題を作成（{quizList.length}問）</h4>
              <button type="button" onClick={addNewQuiz}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-add-line text-sm"></i>問題を追加
              </button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {quizList.map((_, idx) => (
                <NavBtn key={idx} index={idx} active={currentIndex === idx} onClick={handleNavClick} />
              ))}
            </div>
          </div>

          {/* 現在の問題フォーム */}
          <QuizForm
            key={currentIndex}
            quiz={currentQuiz}
            index={currentIndex}
            totalCount={quizList.length}
            onUpdate={handleQuizUpdate}
            onDelete={deleteQuiz}
          />

          {/* アクションボタン */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap">
              キャンセル
            </button>
            <button type="button" onClick={handleSubmit} disabled={saving}
              className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
              {saving ? '保存中...' : `${quizList.length}問を保存する`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
